"use client";

import { useState } from "react";
import {
  Button,
  CloseButton,
  Drawer,
  HStack,
  Icon,
  Avatar,
  Link as ChakraLink,
  Portal,
  Text,
  defineStyle,
} from "@chakra-ui/react";
import { NavLinks } from "./NavLinks";
import KidsNavigationBar from "./KidsNavigationBar";
import CommunityNavigationBar from "./CommunityNavigationBar";
import DownloadsNavigationBar from "./DownloadsNavigationBar";
import NotificationBell from "./NotificationBell";
import { LuExternalLink } from "react-icons/lu";
import { FaYoutube } from "react-icons/fa6";
import { ColorModeButton } from "@/components/ui/color-mode";
import { useSession, signOut } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useLoading } from "@/app/context/LoadingProvider";
import { useNavigationGuard } from "@/app/context/NavigationGuardProvider";
import { KoFiButton } from "./KoFiButton";



const ringCss = defineStyle({
  outlineWidth: "2px",
  outlineColor: "colorPalette.500",
  outlineOffset: "2px",
  outlineStyle: "solid",
});

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { data: session, status } = useSession();
  const isLoggedIn = status === "authenticated";
  const { setPageLoading } = useLoading();
  const { guardedPush } = useNavigationGuard();
  const pathname = usePathname();
  const fullName = session?.user?.name ?? "User";
  const firstName = fullName.split(/\s+/)[0] || "User";
  const userImage =
    session?.user?.isMinor !== true && session?.user?.showProfileImage !== false
      ? session?.user?.image ?? undefined
      : undefined;
  
  async function handleSignOut() {
  try {
    setSigningOut(true);
    setOpen(false);
    setPageLoading(true);

    await signOut({
      redirectTo: "/",
    });
  } catch (error) {
    console.error("[SIGN_OUT_ERROR]", error);
    setPageLoading(false);
    setSigningOut(false);
  }
}

  return (
    <div>
      <Drawer.Root open={open} onOpenChange={(details) => setOpen(details.open)}>
        <Drawer.Trigger asChild>
          <Avatar.Root size="2xs" cursor="pointer" css={ringCss}>
            <Avatar.Fallback name={firstName} />
            {userImage ? <Avatar.Image src={userImage} /> : null}
          </Avatar.Root>
        </Drawer.Trigger>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header>
                <Drawer.Title fontSize="sm">{isLoggedIn ? `Welcome, ${firstName}` : "Welcome"}</Drawer.Title>
              </Drawer.Header>
              <Drawer.Body>
                <HStack pb={3} mb={3} borderBottom="1px solid" borderColor="border.muted" gap={2}>
                  <Button
                    size="xs"
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    px={3}
                    py={1.5}
                    rounded="full"
                    border="1px solid"
                    borderColor="blackAlpha.200"
                    bg="blackAlpha.50"
                    _dark={{ borderColor: "whiteAlpha.300", bg: "whiteAlpha.100" }}
                    fontSize="sm"
                    fontWeight="semibold"
                    color={pathname.startsWith("/blog") ? "teal.500" : "fg"}
                    _hover={{
                      textDecoration: "none",
                      color: "teal.500",
                      bg: "blackAlpha.100",
                      _dark: { bg: "whiteAlpha.200" },
                    }}
                    onClick={() => {
                      setOpen(false);
                      guardedPush("/blog");
                    }}
                  >
                    <HStack gap={1.5}>
                      <Text as="span">Blog</Text>
                      <Icon as={LuExternalLink} boxSize="4" />
                    </HStack>
                  </Button>
                  <ChakraLink
                    href="https://www.youtube.com/@BongGo2026"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="red.600"
                    cursor="pointer"
                    aria-label="Visit BongGo on YouTube"
                    _hover={{ color: "red.500" }}
                    _focusVisible={{ color: "red.500", outline: "none" }}
                  >
                    <Icon as={FaYoutube} boxSize={6} display="block" />
                  </ChakraLink>
                  {isLoggedIn ? <NotificationBell inOverlay /> : null}
                </HStack>
                <NavLinks variant="sidebar" onNavigate={() => setOpen(false)} />
                <div style={{ marginTop: "1rem" }}>
                  <KidsNavigationBar onNavigate={() => setOpen(false)} />
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <CommunityNavigationBar onNavigate={() => setOpen(false)} />
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <DownloadsNavigationBar onNavigate={() => setOpen(false)} />
                </div>
              </Drawer.Body>
              <Drawer.Footer>
                <KoFiButton onClick={() => setOpen(false)} />
                <ColorModeButton />
                {isLoggedIn && (
                  <Button
                    size="xs"
                    variant="solid"
                    colorPalette="red"
                    onClick={handleSignOut}
                    loading={signingOut}
                    disabled={signingOut}
                  >
                    Sign Out
                  </Button>
                )}
              </Drawer.Footer>
              <Drawer.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Drawer.CloseTrigger>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </div>
  );
};

export default MobileNav;
